import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageHero } from "@/components/shared/PageHero";
import { CTASection } from "@/components/shared/CTASection";
import { Calendar, Clock, ArrowRight, Loader2 } from "lucide-react";
import { format } from "date-fns";

// Import lawn care images
import aerationOverseeding from "@/assets/services/aeration-overseeding.jpg";
import dethatching from "@/assets/services/dethatching.jpg";
import mulchingBeds from "@/assets/services/mulching-beds.jpg";
import snowRemoval from "@/assets/services/snow-removal.jpg";
import seasonalCleanup from "@/assets/services/seasonal-cleanup.jpg";
import lawnMowingMistakes from "@/assets/blog/lawn-mowing-mistakes.jpg";

// Default placeholder image for posts without images
export const defaultBlogPostImage = aerationOverseeding;

export const staticBlogPosts = [
  {
    slug: "spring-lawn-care-checklist",
    title: "Spring Lawn Care Checklist: Get Your Yard Ready for the Season",
    excerpt: "Essential spring lawn care tasks to help your grass recover from winter and thrive all season long.",
    created_at: "2026-01-08",
    read_time: "5 min read",
    category: "Lawn Care",
    image_url: seasonalCleanup,
  },
  {
    slug: "benefits-of-mulching",
    title: "5 Benefits of Mulching Your Garden Beds",
    excerpt: "Discover why professional mulching is one of the best investments you can make for your landscape.",
    created_at: "2026-01-05",
    read_time: "4 min read",
    category: "Landscaping",
    image_url: mulchingBeds,
  },
  {
    slug: "when-to-aerate-lawn",
    title: "When Is the Best Time to Aerate Your Minnesota Lawn?",
    excerpt: "Learn the optimal timing for lawn aeration in Minnesota and why it's crucial for a healthy lawn.",
    created_at: "2026-01-02",
    read_time: "4 min read",
    category: "Lawn Care",
    image_url: aerationOverseeding,
  },
  {
    slug: "snow-removal-tips-homeowners",
    title: "Snow Removal Tips for Minnesota Homeowners",
    excerpt: "Stay safe this winter with these essential snow removal tips and learn when to call the professionals.",
    created_at: "2025-12-22",
    read_time: "4 min read",
    category: "Snow Removal",
    image_url: snowRemoval,
  },
  {
    slug: "lawn-mowing-mistakes",
    title: "7 Common Lawn Mowing Mistakes to Avoid",
    excerpt: "Are you making these lawn mowing mistakes? Learn how to mow properly for a healthier lawn.",
    created_at: "2025-12-08",
    read_time: "4 min read",
    category: "Lawn Care",
    image_url: lawnMowingMistakes,
  },
];


const Blog = () => {
  const { data: dbPosts, isLoading } = useQuery({
    queryKey: ['blog-posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('published', true)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  // Combine database posts with static posts
  const allPosts = [
    ...(dbPosts || []),
    ...staticBlogPosts,
  ];

  return (
    <>
      <Helmet>
        <title>Lawn Care & Landscaping Blog | Riekels Lawn Care</title>
        <meta
          name="description"
          content="Expert tips on lawn care, landscaping, and snow removal from Riekels Lawn Care & Landscaping. Learn how to maintain a beautiful Minnesota lawn."
        />
      </Helmet>
      <main>
        <PageHero
          badge="Our Blog"
          title="Lawn Care Tips & Guides"
          subtitle="Expert advice on maintaining a beautiful lawn and landscape."
        />

        <section className="py-20">
          <div className="container mx-auto px-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-secondary" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {allPosts.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="group"
                  >
                    <article className="feature-card h-full flex flex-col hover:border-secondary transition-colors overflow-hidden p-0">
                      {/* Image */}
                      <div className="aspect-video overflow-hidden bg-muted">
                        <img
                          src={post.image_url || defaultBlogPostImage}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      
                      <div className="p-6 flex flex-col flex-1">
                        <div className="mb-4">
                          <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-secondary/10 text-secondary">
                            {post.category}
                          </span>
                        </div>
                        <h2 className="text-xl font-heading font-bold text-foreground mb-3 group-hover:text-secondary transition-colors">
                          {post.title}
                        </h2>
                        <p className="text-muted-foreground mb-4 flex-1">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-sm text-muted-foreground pt-4 border-t border-border">
                          <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {format(new Date(post.created_at), 'MMMM d, yyyy')}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {post.read_time}
                            </span>
                          </div>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <CTASection />
      </main>
    </>
  );
};

export default Blog;
